"use client";

import { useState, type RefObject } from "react";

import type { AppIcon } from "@/components/appIcons";
import { filterOptionsBySearch } from "@/components/ui/filter-options-by-search";
import { filterSelectSelection } from "@/components/ui/filter-select-selection";
import { FilterSelectTrigger } from "@/components/ui/filter-select-trigger";
import { FilterSelectOption } from "@/components/ui/FilterSelectOption";
import { selectSurfaceLayout } from "@/components/ui/select-surface-layout";
import { cn } from "@/lib/utils";

import { SearchableSelectSurface } from "./searchable-select-surface";

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
  /** Optional semantic icon shown before the label. */
  icon?: AppIcon;
  /** Optional small image URL (e.g. flag) shown before the label. */
  iconUrl?: string;
}

interface FilterSelectProps {
  icon: AppIcon;
  placeholder: string;
  options: FilterOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  className?: string;
  clearable?: boolean;
  intent?: "filter" | "sort";
  filterSearchOptions?: boolean;
  onSearchChange?: (value: string) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  selectedLabelPrefix?: string;
  showTriggerIcon?: boolean;
  triggerClassName?: string;
  triggerLabelClassName?: string;
  variant?: "compact" | "fullWidth";
}

export function FilterSelect({
  icon,
  placeholder,
  options,
  value,
  onChange,
  className,
  clearable = true,
  intent = "filter",
  filterSearchOptions = true,
  onSearchChange,
  searchable,
  searchPlaceholder,
  selectedLabelPrefix,
  showTriggerIcon = true,
  triggerClassName,
  triggerLabelClassName,
  variant = "compact",
}: FilterSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const isFullWidth = variant === "fullWidth";

  const { option: selectedOption, label: selectedLabel } = filterSelectSelection(options, value);
  const surfaceLayout = selectSurfaceLayout(
    "w-[min(22rem,calc(100vw-2rem))] bg-card rounded-md",
    "w-[220px]",
    isFullWidth,
  );

  const setOpenState = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setSearch("");
      onSearchChange?.("");
    }
  };

  const filteredOptions = filterOptionsBySearch(options, searchable && filterSearchOptions, search);

  const renderOptionList = (className: string, mode: "desktop" | "mobile") => {
    const isMobileSort = intent === "sort" && mode === "mobile";

    return (
      <div
        className={cn(
          "overflow-y-auto overscroll-contain flex flex-col",
          isMobileSort && "gap-2 p-2",
          className,
        )}
        role={isMobileSort ? "radiogroup" : undefined}
        aria-label={isMobileSort ? placeholder : undefined}
      >
        {filteredOptions.map((option) => (
          <FilterSelectOption
            key={option.value}
            clearable={clearable}
            isMobileSort={isMobileSort}
            onChange={onChange}
            onClose={() => setOpen(false)}
            option={option}
            selectedValue={value}
          />
        ))}
      </div>
    );
  };

  const renderSearchInput = (ref: RefObject<HTMLInputElement | null>) =>
    searchable ? (
      <div className="p-2 border-b border-border">
        <input
          ref={ref}
          type="text"
          value={search}
          autoFocus
          onChange={(event) => {
            setSearch(event.target.value);
            onSearchChange?.(event.target.value);
          }}
          placeholder={searchPlaceholder ?? placeholder}
          className="w-full max-sm:min-h-11 px-2.5 py-2 bg-secondary border border-border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>
    ) : null;

  return (
    <SearchableSelectSurface
      open={open}
      onOpenChange={setOpenState}
      title={placeholder}
      icon={icon}
      containerClassName={className}
      shouldFocus={searchable}
      desktopPanelClassName={surfaceLayout.desktopPanelClassName}
      desktopOptionsClassName={surfaceLayout.desktopOptionsClassName}
      renderTrigger={({ open: isOpen, setOpen: setSurfaceOpen }) => (
        <FilterSelectTrigger
          open={isOpen}
          setOpen={setSurfaceOpen}
          icon={icon}
          selectedIconUrl={selectedOption?.iconUrl}
          selectedIcon={selectedOption?.icon}
          selectedLabel={selectedLabel}
          selectedLabelPrefix={selectedLabelPrefix}
          placeholder={placeholder}
          value={value}
          clearable={clearable}
          intent={intent}
          isFullWidth={isFullWidth}
          showTriggerIcon={showTriggerIcon}
          triggerClassName={triggerClassName}
          triggerLabelClassName={triggerLabelClassName}
          onClear={() => {
            onChange(null);
            setOpenState(false);
          }}
        />
      )}
      renderInput={renderSearchInput}
      renderOptions={(_, className, mode) => renderOptionList(className, mode)}
    />
  );
}
